import axios from "axios";
import localStorage from "~/services/localStorage";

const runtimeMode = import.meta.env?.MODE || process.env?.NODE_ENV;
const isDevOrTest = runtimeMode === "development" || runtimeMode === "test";
const isProd = runtimeMode === "production";

/**
 * Validates URL to prevent SSRF and misconfiguration
 */
const validateUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    if (!["http:", "https:"].includes(parsed.protocol)) {
      throw new Error(`Invalid protocol: ${parsed.protocol}. Only HTTP and HTTPS are allowed.`);
    }
    // Optional: add allowlist check for production
    // if (isProd && !ALLOWED_DOMAINS.includes(parsed.hostname)) {
    //   throw new Error(`Domain not allowed: ${parsed.hostname}`);
    // }
    return url.replace(/\/$/, "");
  } catch (err) {
    throw new Error(`Invalid Support Tools base URL: ${url}. ${err instanceof Error ? err.message : ""}`);
  }
};

/**
 * Dynamic base URL resolution with production-safe fallback handling
 * Priority: ENV variable > window.location.origin > guarded localhost (dev/test only)
 */
const getBaseUrl = () => {
  // 1. Use explicit environment variable if provided
  if (import.meta.env.VITE_SUPPORT_TOOLS_BASE_URL) {
    return validateUrl(import.meta.env.VITE_SUPPORT_TOOLS_BASE_URL);
  }

  // 2. Fallback to current origin for same-origin requests (production builds)
  if (typeof window !== "undefined") {
    return validateUrl(window.location.origin);
  }

  // 3. Final fallback - only allow localhost in development/test environments
  if (isDevOrTest) {
    return "http://localhost:8700";
  }

  // In production SSR, we must have an explicit base URL configured
  throw new Error(
    "Support Tools API base URL not configured for production SSR. " +
      "Please set VITE_SUPPORT_TOOLS_BASE_URL environment variable.",
  );
};

const SUPPORT_TOOLS_BASE_URL = getBaseUrl();

export const supportToolsApi = axios.create({
  baseURL: SUPPORT_TOOLS_BASE_URL,
  timeout: 30000, // 30 second default timeout
  headers: {
    "Content-Type": "application/json",
  },
});

supportToolsApi.interceptors.request.use((config) => {
  const accessToken = localStorage.getAccessToken();

  if (!accessToken) {
    if (isProd) {
      return Promise.reject(new Error("User is not authenticated with Okta - please log in"));
    }
    console.warn("No Okta access token found - API requests may fail with 401/403");
  }

  if (accessToken) {
    config.headers.set("Authorization", `Bearer ${accessToken}`);
  }

  return config;
});

// Response interceptor to extract error messages from API responses
supportToolsApi.interceptors.response.use(
  (response) => response,
  (error) => {
    let errorMessage: string | undefined;

    // Try to extract error message from response
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      errorMessage = typeof detail === "string" ? detail : JSON.stringify(detail);
    } else if (error.response?.data?.message) {
      const message = error.response.data.message;
      errorMessage = typeof message === "string" ? message : JSON.stringify(message);
    }

    // Fallback to status text or generic message
    if (!errorMessage) {
      errorMessage = error.response?.statusText || `Request failed with status ${error.response?.status || "unknown"}`;
    }

    error.message = errorMessage;
    return Promise.reject(error);
  },
);
